import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import {
  SDUIImageElementRenderer,
  ISDUIImageElementRendererProps,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Image/SDUIImageElementRenderer";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { SDUITypographyRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { cn } from "@faire/web--source/tools/cn";
import { IImageElement } from "@faire/web-api--source/indigofair/data/IImageElement";
import { IImageFanTile } from "@faire/web-api--source/indigofair/data/IImageFanTile";
import React from "react";

const FRONT_IMAGE_TILE_WIDTH = "56px";
const BACK_IMAGE_TILE_WIDTH = "44px";
const DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH = 250;

// Design has not fully made a decision on some aspects of the new ImageFanTile.
// While the layout is mostly confirmed, the animations are still to be discussed.
// To avoid unwanted designs on category page usage of this tile (which is closer to launch)
// but still unblock development of the new ImageFanTile, we will keep this version for now.
// TODO(alexieyizhe FD-271553): Remove this once we have design approval to propogate visual changes for ImageFanTile to category page
export const SDUIImageFanTileRenderer_LEGACY = ({
  tileElement,
  loggingData,
  renderBehaviour,
}: SDUITileInstanceRendererProps) => {
  const { on_click, image_fan_tile } = tileElement;
  if (!image_fan_tile) {
    return null;
  }

  const { primary_text, image_elements, image_fan_tile_style } = image_fan_tile;
  if (!image_elements[0] || !primary_text || !image_fan_tile_style) {
    return null;
  }
  const { animation_on_hover } = image_fan_tile_style;
  const hasHighlightBorderAnimation = animation_on_hover?.includes(
    IImageFanTile.ImageFanTileAnimation.HIGHLIGHT_BORDER
  );
  const hasFanOutImagesAnimation = animation_on_hover?.includes(
    IImageFanTile.ImageFanTileAnimation.FAN_OUT_IMAGES
  );

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
    >
      <Row
        className={cn(
          "group border-core-border-muted rounded-sm border border-solid p-[16px]",
          hasHighlightBorderAnimation ? "hover:border-core-surface-hover" : null
        )}
      >
        <div
          className={cn(
            "z-3 transition duration-300",
            hasFanOutImagesAnimation ? "group-hover:-rotate-3" : null
          )}
        >
          <SDUIImageElementRenderer
            {...getImageElementProps({
              imageElement: image_elements[0],
              isFrontImage: true,
              loggingData,
              renderBehaviour,
            })}
          />
        </div>
        {image_elements[1] ? (
          <div
            className={cn(
              "z-2 -ml-[28px] rotate-14 transform self-center transition duration-300",
              hasFanOutImagesAnimation
                ? "group-hover:translate-x-[8px] group-hover:translate-y-[1px] group-hover:rotate-22"
                : null
            )}
          >
            <SDUIImageElementRenderer
              {...getImageElementProps({
                imageElement: image_elements[1],
                isFrontImage: false,
                loggingData,
                renderBehaviour,
              })}
            />
          </div>
        ) : null}
        <Spacer width="3x" />
        <SDUITypographyRenderer
          textElement={primary_text}
          loggingData={loggingData}
        />
      </Row>
    </SDUIOnClickHandler>
  );
};

const getImageElementProps = ({
  imageElement,
  isFrontImage,
  ...restTileRendererProps
}: {
  imageElement: IImageElement;
  isFrontImage: boolean;
} & Pick<
  SDUITileInstanceRendererProps,
  "loggingData" | "renderBehaviour"
>): ISDUIImageElementRendererProps => {
  return {
    ...restTileRendererProps,
    imageElement,
    imageStyles: {
      aspectRatio: 1,
      width: isFrontImage ? FRONT_IMAGE_TILE_WIDTH : BACK_IMAGE_TILE_WIDTH,
      height: "auto",
      isCornersRounded: true,
    },
    optimizeImageOptions: {
      resizeMethod: "fill",
      width: imageElement.image?.width ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH,
      height: imageElement.image?.height ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH,
    },
  };
};
